import { useEffect, useRef, useCallback } from 'react';

interface MagneticOptions {
  strength?: number;
  radius?: number;
  ease?: number;
}

export const useMagnetic = <T extends HTMLElement>({
  strength = 0.3,
  radius = 100,
  ease = 0.15,
}: MagneticOptions = {}) => {
  const ref = useRef<T>(null);
  const animationFrame = useRef<number>();
  const currentX = useRef(0);
  const currentY = useRef(0);
  const targetX = useRef(0);
  const targetY = useRef(0);
  const cachedCenter = useRef<{ x: number; y: number } | null>(null);
  const isHovering = useRef(false);

  // Cache element center position
  const updateCachedCenter = useCallback(() => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      cachedCenter.current = {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      };
    }
  }, []);

  const animate = useCallback(() => {
    if (!ref.current) return;

    currentX.current += (targetX.current - currentX.current) * ease;
    currentY.current += (targetY.current - currentY.current) * ease;

    ref.current.style.transform = `translate(${currentX.current}px, ${currentY.current}px)`;

    if (
      Math.abs(targetX.current - currentX.current) > 0.01 ||
      Math.abs(targetY.current - currentY.current) > 0.01
    ) {
      animationFrame.current = requestAnimationFrame(animate);
    } else {
      animationFrame.current = undefined;
    }
  }, [ease]);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const handleMouseMove = (e: MouseEvent) => {
      // Only update center when hovering to reduce layout reads
      if (!cachedCenter.current) {
        updateCachedCenter();
      }

      if (!cachedCenter.current) return;

      const distanceX = e.clientX - cachedCenter.current.x;
      const distanceY = e.clientY - cachedCenter.current.y;
      const distance = Math.sqrt(distanceX ** 2 + distanceY ** 2);

      if (distance < radius) {
        if (!isHovering.current) {
          isHovering.current = true;
          // Recache center when entering radius
          updateCachedCenter();
        }
        const power = (1 - distance / radius) * strength;
        targetX.current = distanceX * power;
        targetY.current = distanceY * power;
      } else {
        isHovering.current = false;
        targetX.current = 0;
        targetY.current = 0;
      }

      if (!animationFrame.current) {
        animationFrame.current = requestAnimationFrame(animate);
      }
    };

    const handleMouseLeave = () => {
      isHovering.current = false;
      cachedCenter.current = null;
      targetX.current = 0;
      targetY.current = 0;
      animationFrame.current = requestAnimationFrame(animate);
    };

    const handleScroll = () => {
      // Invalidate cache on scroll
      cachedCenter.current = null;
    };

    document.addEventListener('mousemove', handleMouseMove);
    element.addEventListener('mouseleave', handleMouseLeave);
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      element.removeEventListener('mouseleave', handleMouseLeave);
      window.removeEventListener('scroll', handleScroll);
      if (animationFrame.current) {
        cancelAnimationFrame(animationFrame.current);
      }
    };
  }, [animate, radius, strength, updateCachedCenter]);

  return ref;
};

export default useMagnetic;
